import {
  Button,
  createStyles,
  Grid,
  Paper,
  type Theme,
  type WithStyles,
  withStyles,
} from "@material-ui/core";
import { useTranslations } from "adapters/i18n";
import { PortalRoutes } from "constants/routes";
import { Show } from "features/shared/components";
import React, { type ComponentType, useState } from "react";
import { RouteComponentProps, withRouter } from "react-router";
import { compose } from "redux";
import {
  activateCategory,
  createCategory,
  deleteCategory,
  updateCategory,
} from "redux/operations";
import type {
  AgeRange,
  EnrichedAnimalCategory,
  WeightGainCurve,
} from "shared/types";
import type {
  CategoryFieldsVisibility,
  CategoryFormState,
} from "../category.types";
import { useFilterForm } from "../hooks/useFilterForm";
import CategoryFilterForm from "./categoryFilterForm";
import CategoryForm from "./categoryForm";
import CategoryListTable from "./categoryListTable";
import ConciliationDateForm from "./conciliationDateForm";

type OwnProps = {
  activateCategory: (
    ...params: Parameters<typeof activateCategory>
  ) => Promise<void>;
  ageRanges: AgeRange[];
  createNewCategory: (
    ...params: Parameters<typeof createCategory>
  ) => Promise<void>;
  conciliationStartDate: string | null;
  deleteCategory: (
    ...params: Parameters<typeof deleteCategory>
  ) => Promise<void>;
  enrichedFarmCategories: EnrichedAnimalCategory[];
  farmWeightGainCurves: WeightGainCurve[];
  fieldsVisibility: CategoryFieldsVisibility;
  updateCategory: (
    ...params: Parameters<typeof updateCategory>
  ) => Promise<void>;
  updateConciliationDate: (date: string) => Promise<void>;
};

type CategoryPageProps = OwnProps &
  WithStyles<typeof styles> &
  RouteComponentProps;

function CategoryPageImpl({
  activateCategory,
  ageRanges,
  enrichedFarmCategories,
  classes,
  createNewCategory,
  conciliationStartDate,
  deleteCategory,
  fieldsVisibility,
  location,
  updateCategory,
  updateConciliationDate,
  farmWeightGainCurves,
}: CategoryPageProps) {
  const { pathname } = location;
  const isHerdControlPage = pathname === PortalRoutes.HERD_CONTROL;

  const [isFormPanelOpen, setIsFormPanelOpen] = useState<boolean>(false);
  const [selectedCategory, setSelectedCategory] =
    useState<EnrichedAnimalCategory | null>(null);

  const { filterForm, handleChangeFilter } = useFilterForm();

  const toggleFormPanel = (isOpen: boolean) => {
    setIsFormPanelOpen(isOpen);
    if (isOpen) {
      return;
    }
    setSelectedCategory(null);
  };

  const handleCloseFormPanel = () => {
    toggleFormPanel(false);
  };

  const handleOpenNewCategoryPanel = () => {
    setSelectedCategory(null);
    toggleFormPanel(true);
  };

  const handleEditCategory = (category: EnrichedAnimalCategory) => {
    setSelectedCategory(category);
    toggleFormPanel(true);
  };

  const handleToggleCategoryStatus = (
    row: EnrichedAnimalCategory,
    isCurrentDeleted: boolean
  ) => {
    if (isCurrentDeleted) {
      activateCategory(row.id);
      return;
    }

    deleteCategory(row.id);
  };

  const handleSubmitCategoryForm = async (formState: CategoryFormState) => {
    if (selectedCategory) {
      await updateCategory(selectedCategory.id, {
        ageRangeId: formState.ageRangeId.value as number,
        desiredNumber: formState.headCount.value,
        gender: formState.gender.value,
        initialNumberConference: formState.initialAmountForConciliation.value,
        name: formState.name.value,
        weightGainCurveId: formState.weightGainCurveId.value,
      });
      toggleFormPanel(false);
      return;
    }

    await createNewCategory({
      ageRangeId: formState.ageRangeId.value as number,
      desiredNumber: formState.headCount.value,
      gender: formState.gender.value,
      initialNumberConference: formState.initialAmountForConciliation.value,
      name: formState.name.value,
      weightGainCurveId: formState.weightGainCurveId.value,
    });

    toggleFormPanel(false);
  };

  return (
    <Paper className={classes.root} square={true}>
      <Grid item sm={12} xs={12} className={classes.containerPage}>
        <Show when={isHerdControlPage}>
          <ConciliationDateForm
            conciliationStartDate={conciliationStartDate}
            updateConciliationDate={updateConciliationDate}
          />
        </Show>
        <CategoryFilterForm
          filterForm={filterForm}
          onChangeFilter={handleChangeFilter}
          showWeightGainCurveFilter={!isHerdControlPage}
        />

        <Show
          when={isFormPanelOpen}
          fallback={
            <AddNewCategoryButton
              classes={classes}
              onClick={handleOpenNewCategoryPanel}
            />
          }
        >
          <CategoryForm
            ageRanges={ageRanges}
            categories={enrichedFarmCategories}
            fieldsVisibility={fieldsVisibility}
            onCancel={handleCloseFormPanel}
            onSubmit={handleSubmitCategoryForm}
            selectedCategory={selectedCategory}
            weightGainCurves={farmWeightGainCurves}
          />
        </Show>
        <CategoryListTable
          categories={enrichedFarmCategories}
          fieldsVisibility={fieldsVisibility}
          filterForm={filterForm}
          isCategoryFormPanelOpen={isFormPanelOpen}
          onEditClick={handleEditCategory}
          onToggleCategoryStatus={handleToggleCategoryStatus}
        />
      </Grid>
    </Paper>
  );
}

type AddNewCategoryButtonProps = {
  onClick: () => void;
} & WithStyles<typeof styles>;

function AddNewCategoryButton({ classes, onClick }: AddNewCategoryButtonProps) {
  const { t } = useTranslations();

  return (
    <Button
      className={classes.newCategoryButton}
      color="primary"
      data-testid="add-new-category-button"
      onClick={onClick}
      variant="contained"
    >
      {t("animalCategory:new-category")}
    </Button>
  );
}

const styles = (theme: Theme) =>
  createStyles({
    root: {
      bottom: 0,
      height: "100%",
      minHeight: "calc(100vh - 90px)",
      overflowX: "auto",
      padding: "46px",
      top: "17%",
      width: "100%",
      zIndex: -1,
    },
    containerPage: {
      backgroundColor: theme.palette.common.white,
      flexGrow: 1,
      height: "100%",
      width: "100%",
    },
    newCategoryButton: {
      borderRadius: "18px",
      float: "right",
      marginBottom: "16px",
      marginRight: 0,
      marginTop: "34px",
    },
  });

const CategoryPage = compose(
  withRouter,
  withStyles(styles)
)(CategoryPageImpl) as ComponentType<OwnProps>;

export default CategoryPage;
